import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles, Shield, Clock, MapPin, Star, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Layout from "@/components/Layout";
import { BUSINESS, SERVICES, REVIEWS } from "@/lib/business";
import heroImg from "@/assets/hero-car-1600.jpg";
import heroImg640 from "@/assets/hero-car-640.jpg";
import heroImg1024 from "@/assets/hero-car-1024.jpg";
import heroImg1600 from "@/assets/hero-car-1600.jpg";
import heroImg640Webp from "@/assets/hero-car-640.webp";
import heroImg1024Webp from "@/assets/hero-car-1024.webp";
import heroImg1600Webp from "@/assets/hero-car-1600.webp";
import heroImg640Avif from "@/assets/hero-car-640.avif";
import heroImg1024Avif from "@/assets/hero-car-1024.avif";
import heroImg1600Avif from "@/assets/hero-car-1600.avif";
import ResponsiveImage from "@/components/ResponsiveImage";
import LazyMap from "@/components/LazyMap";
import { canonicalLink, DEFAULT_OG_DESCRIPTION, DEFAULT_OG_TITLE, OG_IMAGE_URL, pageSocialMeta, SITE_URL } from "@/lib/seo";

const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "AutoDetailing",
  "@id": `${SITE_URL}/#business`,
  name: "BLVDS Detailing",
  alternateName: "BLVDS Mobile Detailing",
  description: DEFAULT_OG_DESCRIPTION,
  url: SITE_URL,
  telephone: "+1-929-379-5527",
  email: "Blvdsdetailing1@gmail.com",
  image: OG_IMAGE_URL,
  logo: OG_IMAGE_URL,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card, Apple Pay, Cash App, Zelle",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Queens",
    addressRegion: "NY",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "City", name: "Queens", containedInPlace: { "@type": "State", name: "New York" } },
    { "@type": "City", name: "Brooklyn", containedInPlace: { "@type": "State", name: "New York" } },
    { "@type": "Place", name: "Long Island" },
    { "@type": "City", name: "Astoria" },
    { "@type": "City", name: "Long Island City" },
    { "@type": "City", name: "Forest Hills" },
    { "@type": "City", name: "Park Slope" },
    { "@type": "City", name: "Williamsburg" },
  ],
  geo: {
    "@type": "GeoCoordinates",
    latitude: 40.7282,
    longitude: -73.7949,
  },
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: 40.7282, longitude: -73.7949 },
    geoRadius: "40000",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "08:00",
      closes: "20:00",
    },
  ],
  sameAs: [
    "https://www.instagram.com/blvds.szn_detailing/",
    "https://www.tiktok.com/@blvds.detailing",
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "4",
    bestRating: "5",
    worstRating: "1",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Mobile Car Detailing Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Basic Wash" },
        price: "50",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Full Detail" },
        price: "120",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Premium Package" },
        price: "250",
        priceCurrency: "USD",
      },
    ],
  },
};

const FAQS = [
  {
    q: "What areas do you serve?",
    a: "BLVDS Detailing is a fully mobile car detailing service covering Queens, Brooklyn, and Long Island. If you're not sure whether your block is in our zone, give us a call.",
  },
  {
    q: "Do I need to provide water or electricity?",
    a: "No. Our mobile detailing rigs are fully self-contained — we bring our own water, power, and premium products to your driveway, office, or curbside spot.",
  },
  {
    q: "How long does each service take?",
    a: "A Basic Wash takes about 45 minutes, a Full Detail takes about 2.5 hours, and our Premium Package with paint correction and ceramic coating takes 6+ hours.",
  },
  {
    q: "How much does mobile car detailing cost in NYC?",
    a: "Our Basic Wash starts at $50, Full Detail is $120, and the Premium Package with ceramic coating is $250. Custom and fleet pricing is available on request.",
  },
  {
    q: "How long does the ceramic coating last?",
    a: "The ceramic coating included in our Premium Package is rated for 12 months of protection with normal use. We use professional-grade Gtechniq products.",
  },
  {
    q: "How do I book an appointment?",
    a: "You can book online in about 60 seconds from our booking page. Same-week appointments are usually available across Queens, Brooklyn, and Long Island.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards, Apple Pay, Cash App, Zelle, and cash on completion.",
  },
] as const;

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Mobile Car Detailing NYC | BLVDS Detailing" },
      { name: "description", content: "Premium mobile car detailing in NYC. We bring showroom-quality washes, full details, and ceramic coatings to driveways across Queens, Brooklyn & Long Island." },
      { name: "google-site-verification", content: "hiEE9EA_dlRifpKEnkKpEWz8Z06mZS2Hs15hapPAgVE" },
      ...pageSocialMeta(
        "/",
        "Mobile Car Detailing NYC | BLVDS Detailing",
        "Premium mobile car detailing in NYC. We bring showroom-quality washes, full details, and ceramic coatings to driveways across Queens, Brooklyn & Long Island.",
      ),
    ],
    links: [canonicalLink("/")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(LOCAL_BUSINESS_JSONLD),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(FAQ_JSONLD),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage
            src={heroImg}
            avif={[
              { src: heroImg640Avif, width: 640 },
              { src: heroImg1024Avif, width: 1024 },
              { src: heroImg1600Avif, width: 1600 },
            ]}
            webp={[
              { src: heroImg640Webp, width: 640 },
              { src: heroImg1024Webp, width: 1024 },
              { src: heroImg1600Webp, width: 1600 },
            ]}
            jpg={[
              { src: heroImg640, width: 640 },
              { src: heroImg1024, width: 1024 },
              { src: heroImg1600, width: 1600 },
            ]}
            sizes="100vw"
            alt="Luxury car detailing in NYC"
            className="w-full h-full object-cover"
            width={1600}
            height={900}
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/15 border border-primary/30 mb-6 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-medium text-primary uppercase tracking-wider">Mobile · Queens · Brooklyn · Long Island</span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.2] mb-6">
              NYC's Premier <br />
              <span className="text-gradient-primary inline-block leading-[1.25] pb-3 pr-2">Car Detailing</span> <br />
              Service
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mb-8">
              Showroom quality washes, full details, and ceramic coatings delivered right to your driveway. Book online in 60 seconds.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:scale-105 transition-smooth"
              >
                Book Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-smooth" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-border bg-surface/50 backdrop-blur-sm font-semibold hover:border-accent/50 hover:bg-surface transition-smooth"
              >
                See Services & Pricing
              </Link>
            </div>

            <div className="flex flex-wrap gap-6 mt-12 text-sm">
              {[
                { icon: Shield, label: "Fully Insured", color: "text-green-500", fill: false },
                { icon: Clock, label: "Same Week Booking", color: "text-red-500", fill: false, innerWhite: true },
                { icon: Star, label: "5 Star Rated", color: "text-yellow-400", fill: true },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-muted-foreground">
                  {b.innerWhite ? (
                    <span className="relative inline-flex w-4 h-4">
                      <b.icon className={`w-4 h-4 ${b.color} absolute inset-0`} />
                      <b.icon
                        className="w-4 h-4 text-white absolute inset-0"
                        style={{ clipPath: "inset(15% 15% 15% 15%)" }}
                      />
                    </span>
                  ) : (
                    <b.icon className={`w-4 h-4 ${b.color}`} fill={b.fill ? "currentColor" : "none"} />
                  )}
                  {b.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">Why BLVDS</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 pr-4 inline-block">Detailing done <span className="text-gradient-accent pr-2">right</span>.</h2>
          <p className="text-muted-foreground">We show up. We detail. You drive away clean.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: "We come to you", desc: "Your home. Your office. Your schedule. We come to you." },
            { icon: Sparkles, title: "Premium products only", desc: "Meguiar's, Chemical Guys, Gtechniq ceramic coatings." },
            { icon: Shield, title: "Satisfaction guaranteed", desc: "We don't stop until it's perfect." },
          ].map((f) => (
            <div key={f.title} className="p-7 rounded-2xl bg-surface border border-border hover:border-primary/30 transition-smooth group">
              <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 group-hover:shadow-glow transition-smooth">
                <f.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-muted-foreground text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">Services</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 pr-4 inline-block">Choose your <span className="text-gradient-primary pr-2">finish</span>.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div
              key={s.id}
              className={`relative p-7 rounded-2xl border transition-smooth ${
                s.highlight
                  ? "bg-gradient-to-b from-primary/10 to-surface border-primary/40 shadow-glow"
                  : "bg-surface border-border hover:border-primary/30"
              }`}
            >
              {s.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-accent text-accent-foreground text-xs font-bold uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              <h3 className="text-2xl font-bold mb-1">{s.name}</h3>
              <p className="text-muted-foreground text-sm mb-4">{s.tagline}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-gradient-primary">${s.price}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-5">Approx. {s.duration}</p>
              <ul className="space-y-2 mb-6">
                {s.features.slice(0, 4).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/book"
                search={{ service: s.id } as any}
                className="flex items-center justify-center w-full min-h-12 py-3 rounded-lg bg-gradient-primary text-primary-foreground font-semibold hover:shadow-glow transition-smooth"
              >
                Book This
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">Reviews</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 pr-4 inline-block">NYC drivers <span className="text-gradient-accent pr-2">love us</span>.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {REVIEWS.map((r) => (
            <div key={r.name} className="p-7 rounded-2xl bg-surface border border-border">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: r.rating }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#FFA500] text-[#FFA500]" />
                ))}
              </div>
              <p className="text-foreground/90 mb-4">{r.text}</p>
              <div className="text-sm">
                <span className="font-semibold">{r.name}</span>
                <span className="text-muted-foreground"> · {r.location}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAP */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">Service Area</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 pr-4 inline-block">We cover your <span className="text-gradient-primary pr-2">block</span>.</h2>
          <p className="text-muted-foreground">Queens, Brooklyn & Long Island. Don't see your neighborhood? Give us a call.</p>
        </div>
        <div className="rounded-2xl overflow-hidden border border-border shadow-elevated">
          <LazyMap
            src={BUSINESS.mapsEmbed}
            title="Service Area Map"
            height={450}
            className="w-full block"
            style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
          />
        </div>
      </section>


      {/* FAQ */}
      <section className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">FAQ</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            Frequently asked <span className="text-gradient-accent pr-2">questions</span>.
          </h2>
        </div>
        <Accordion type="single" collapsible className="rounded-2xl bg-surface border border-border px-6">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden p-10 sm:p-16 bg-gradient-to-br from-primary/20 via-surface to-accent/15 border border-primary/30">
          <div className="relative max-w-2xl">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Dirty cars don't belong in New York.</h2>
            <p className="text-lg text-muted-foreground mb-8">Same week appointments available across Queens, Brooklyn & Long Island.</p>
            <Link to="/book" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:scale-105 transition-smooth">
              Book Your Detail <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
